import React, { useState, useEffect, useCallback } from 'react';
import { ArrowLeft, Clock, CheckCircle, AlertCircle, Save, Plus } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { questionnaireService } from '../services/questionnaireService';
import { templateService } from '../services/templateService';
import ProjectCard from './ProjectCard';
import type { QuestionnaireResponse, QuestionnaireAssignment, Project, QuestionnaireTemplate } from '../types/questionnaires';

interface QuestionnaireFilloutProps {
  assignmentId: string;
}

const QuestionnaireFillout: React.FC<QuestionnaireFilloutProps> = ({ assignmentId }) => {
  const { user, userProfile } = useAuth();
  const [assignment, setAssignment] = useState<QuestionnaireAssignment | null>(null);
  const [template, setTemplate] = useState<QuestionnaireTemplate | null>(null);
  const [response, setResponse] = useState<Partial<QuestionnaireResponse>>({});
  const [isReadOnly, setIsReadOnly] = useState(false);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Load assignment, questionnaire, and existing response
  useEffect(() => {
    loadData();
  }, [assignmentId, user]);

  // Auto-save every 10 seconds
  useEffect(() => {
    if (!isReadOnly && response.id && !isSaving) {
      const interval = setInterval(() => {
        autoSave();
      }, 10000); // 10 seconds
      
      return () => clearInterval(interval);
    }
  }, [response, isReadOnly, isSaving]);

  const loadData = async () => {
    if (!user) return;
    
    try {
      setIsLoading(true);
      setError(null);
      
      // Load assignment
      const assignmentData = await questionnaireService.getAssignment(assignmentId);
      if (!assignmentData) {
        setError('Fragebogen-Zuweisung nicht gefunden');
        return;
      }
      setAssignment(assignmentData);
      
      // Load template
      const templates = await templateService.listTemplates();
      const assignmentTemplate = templates.find(t => t.id === assignmentData.templateId);
      if (!assignmentTemplate) {
        setError('Fragebogen-Vorlage nicht gefunden');
        return;
      }
      setTemplate(assignmentTemplate);
      
      // Load existing response if any
      const existingResponse = await questionnaireService.getResponse(assignmentId, user.uid);
      
      if (existingResponse) {
        setResponse(existingResponse);
        setLastSaved(existingResponse.lastSaved);
      } else {
        // Initialize new response with one empty project
        setResponse({
          assignmentId,
          questionnaireId: assignmentData.questionnaireId,
          templateId: assignmentData.templateId,
          userId: user.uid,
          userEmail: user.email || '',
          userName: userProfile?.displayName || user.displayName || '',
          projects: [{ id: `proj_${Date.now()}`, fieldValues: {} }], // Start with one empty project
          isSubmitted: false
        });
      }
      
      // Check if deadline passed or already submitted
      const isPastDeadline = new Date() > new Date(assignmentData.deadline);
      const isSubmitted = assignmentData.status === 'submitted' || existingResponse?.isSubmitted || false;
      setIsReadOnly(isPastDeadline || isSubmitted);
      
      // Update assignment status to in_progress if not already
      if (assignmentData.status === 'assigned' && !isPastDeadline) {
        await questionnaireService.updateAssignmentStatus(assignmentId, 'in_progress');
      }
      
    } catch (err) {
      console.error('Error loading questionnaire:', err);
      setError('Fehler beim Laden des Fragebogens');
    } finally {
      setIsLoading(false);
    }
  };

  const autoSave = useCallback(async () => {
    if (!user || isReadOnly || isSaving || !response.assignmentId) return;
    
    try {
      setIsSaving(true);
      const responseId = await questionnaireService.saveResponse(response, true);
      
      // Update response with ID if it was just created
      if (!response.id) {
        setResponse(prev => ({ ...prev, id: responseId }));
      }
      
      setLastSaved(new Date());
    } catch (err) {
      console.error('Auto-save error:', err);
      // Don't show error to user for auto-save failures
    } finally {
      setIsSaving(false);
    }
  }, [response, user, isReadOnly, isSaving]);

  const addProject = () => {
    if (isReadOnly) return;
    
    const newProject: Project = {
      id: `proj_${Date.now()}`,
      fieldValues: {}
    };
    
    setResponse(prev => ({
      ...prev,
      projects: [...(prev.projects || []), newProject]
    }));
  };

  const removeProject = (projectId: string) => {
    if (isReadOnly) return;
    
    setResponse(prev => ({
      ...prev,
      projects: (prev.projects || []).filter(p => p.id !== projectId)
    }));
  };

  const updateProject = (projectId: string, updates: Partial<Project>) => {
    if (isReadOnly) return;
    
    setResponse(prev => ({
      ...prev,
      projects: (prev.projects || []).map(p => 
        p.id === projectId ? { ...p, ...updates } : p
      )
    }));
  };

  const handleSubmit = async () => {
    if (!user || !assignment || !template) return;
    
    // Validate that at least one project exists
    if (!response.projects || response.projects.length === 0) {
      alert('Bitte fügen Sie mindestens ein Projekt hinzu.');
      return;
    }
    
    // Validate required fields from template
    const requiredFields = template.fields.filter(f => f.required);
    const incompleteProjects = response.projects.filter((project) => {
      return requiredFields.some(field => !project.fieldValues?.[field.id]?.trim());
    });
    
    if (incompleteProjects.length > 0) {
      alert('Bitte füllen Sie alle Pflichtfelder in allen Projekten aus.');
      return;
    }
    
    const confirmed = window.confirm(
      'Möchten Sie den Fragebogen wirklich abschicken? Nach dem Abschicken können Sie ihn nicht mehr bearbeiten.'
    );
    
    if (!confirmed) return;
    
    try {
      setIsSaving(true);
      
      // Save one last time with all data (this will create the response if it doesn't exist)
      const responseId = await questionnaireService.saveResponse(response, false);
      
      // Update response with ID if it was just created
      if (!response.id) {
        setResponse(prev => ({ ...prev, id: responseId }));
      }
      
      // Mark as submitted
      await questionnaireService.submitResponse(responseId, assignmentId);
      
      alert('Fragebogen erfolgreich abgeschickt! Vielen Dank für Ihre Antworten.');
      
      // Redirect to dashboard
      window.location.href = '/my-tasks';
      
    } catch (err) {
      console.error('Submit error:', err);
      alert('Fehler beim Abschicken. Bitte versuchen Sie es erneut.');
    } finally {
      setIsSaving(false);
    }
  };

  const formatDeadline = (deadline: Date) => {
    return new Date(deadline).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const daysUntilDeadline = assignment 
    ? Math.ceil((new Date(assignment.deadline).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24))
    : 0;

  if (isLoading) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
          <span className="ml-4 text-gray-600">Lade Fragebogen...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="bg-red-50 border-l-4 border-red-500 p-4">
          <div className="flex items-center">
            <AlertCircle className="w-6 h-6 text-red-600 mr-3" />
            <p className="text-red-800">{error}</p>
          </div>
        </div>
        <button
          onClick={() => window.location.href = '/'}
          className="mt-4 text-purple-600 hover:text-purple-700 flex items-center"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Zurück zur Übersicht
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto p-6">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => window.location.href = '/'}
          className="text-purple-600 hover:text-purple-700 flex items-center mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Zurück zur Übersicht
        </button>
        
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          {assignment?.questionnaireTitle || 'Fragebogen'}
        </h1>
        
        {/* Deadline warning */}
        {assignment && !isReadOnly && (
          <div className={`flex items-center text-sm ${daysUntilDeadline <= 3 ? 'text-red-600' : 'text-gray-600'}`}>
            <Clock className="w-4 h-4 mr-2" />
            <span>
              Deadline: {formatDeadline(assignment.deadline)}
              {daysUntilDeadline > 0 && ` (noch ${daysUntilDeadline} Tag${daysUntilDeadline !== 1 ? 'e' : ''})`}
            </span>
          </div>
        )}
      </div>

      {/* Read-only notice */}
      {isReadOnly && (
        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-6">
          <div className="flex items-center">
            <AlertCircle className="w-6 h-6 text-yellow-600 mr-3" />
            <div>
              <p className="text-yellow-800 font-semibold">Dieser Fragebogen kann nicht mehr bearbeitet werden</p>
              <p className="text-yellow-700 text-sm">
                {assignment?.status === 'submitted' 
                  ? 'Sie haben diesen Fragebogen bereits abgeschickt.' 
                  : 'Die Deadline ist überschritten.'}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Auto-save indicator */}
      {!isReadOnly && lastSaved && (
        <div className="flex items-center justify-end text-sm text-gray-500 mb-4">
          {isSaving ? (
            <>
              <Save className="w-4 h-4 mr-2 animate-pulse" />
              <span>Speichert...</span>
            </>
          ) : (
            <>
              <CheckCircle className="w-4 h-4 mr-2 text-green-600" />
              <span>Automatisch gespeichert: {lastSaved.toLocaleTimeString('de-DE')}</span>
            </>
          )}
        </div>
      )}

      {/* Info banner */}
      <div className="bg-purple-50 border-l-4 border-purple-500 p-4 mb-6">
        <p className="text-purple-900 font-medium">
          Fügen Sie alle relevanten Projekte hinzu
        </p>
        <p className="text-purple-700 text-sm mt-1">
          Sie können beliebig viele Projekte hinzufügen. Jedes Projekt wird separat erfasst.
        </p>
      </div>

      {/* Projects */}
      <div className="space-y-6">
        {(response.projects || []).map((project, index) => (
          <ProjectCard
            key={project.id}
            project={project}
            projectNumber={index + 1}
            totalProjects={response.projects?.length || 0}
            templateFields={template?.fields || []}
            isReadOnly={isReadOnly}
            onChange={(updates) => updateProject(project.id, updates)}
            onRemove={() => removeProject(project.id)}
          />
        ))}
      </div>

      {/* Add Project Button */}
      {!isReadOnly && (
        <div className="mt-6 flex justify-center">
          <button
            onClick={addProject}
            className="bg-white border-2 border-dashed border-purple-300 text-purple-700 px-6 py-4 rounded-lg hover:border-purple-500 hover:bg-purple-50 transition-colors flex items-center gap-2 font-medium"
          >
            <Plus className="w-5 h-5" />
            Weiteres Projekt hinzufügen
          </button>
        </div>
      )}

      {/* Submit button */}
      {!isReadOnly && (
        <div className="mt-8 flex justify-end">
          <button
            onClick={handleSubmit}
            disabled={isSaving}
            className="bg-purple-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center shadow-lg"
          >
            <CheckCircle className="w-5 h-5 mr-2" />
            Fragebogen abschicken ({(response.projects || []).length} Projekt{(response.projects || []).length !== 1 ? 'e' : ''})
          </button>
        </div>
      )}
    </div>
  );
};

export default QuestionnaireFillout;

